import _ from 'lodash';
import { createAction as reduxActCreateAction } from 'redux-act';
import * as Sentry from '@sentry/react';
import { compose } from 'react-recompose';
import browserHistory from 'history/browser';

import analyticsService from '../../common/Services/AnalyticsService';

/**
 * Takes a namespace and prepends this to the "description" of each of the action creators returned
 * @param {string} namespace - the namespace for all actions created by this factory (e.g. "Experience Workspace => experiences => ")
 * @returns {{ createAction, createApiFailureAction }} an object of action creators
 */
export const createActionFactory = (namespace) => {
  const _namespace = compose(_addTrailingSpace, _addNamespaceDelimiter, _.trimEnd)(namespace);
  return {
    createAction:           createAction(_namespace),
    createTrackedAction:    createTrackedAction(_namespace),
    createApiFailureAction: createApiFailureAction(_namespace),
  };
};

/**
 * Your standard create action
 */
const createAction = (namespace) => (description, payloadReducer, metaReducer) =>
  reduxActCreateAction(namespace + description, payloadReducer, metaReducer);

/**
 * an action that will automatically be tracked by the console tracking reducer
 */
const createTrackedAction =
  (namespace) =>
    (description, payloadReducer, metaReducer = (...args) => ({ ...args })) =>
      reduxActCreateAction(namespace + description, payloadReducer, (...rest) => ({
        tracking: {
          event: namespace + description,
          type:  analyticsService.trackingTypes.ACTION,
          route: browserHistory.location.pathname,
          ...metaReducer(...rest),
        },
      }));

/**
 * Action creator for API failure actions.
 */
const createApiFailureAction =
  (namespace) =>
    (description, payloadReducer = (...args) => ({ ...args })) =>
      reduxActCreateAction(
        namespace + description,
        (error, ...rest) => {
        // isInitialized() returns false both when Sentry.init() was never called (no DSN) and when init() itself failed
          if (Sentry.isInitialized()) {
            Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
          }
          return rest ? { ...payloadReducer(...rest), error } : { error };
        },
        () => ({ isApiError: true })
      );

// ######################################################################################## //
// PRIVATE HELPERS
// ######################################################################################## //

/**
 * Ensures the namespace has the delimiter "=>"
 * @param {string} namespace
 * @returns {string}
 */
const _addNamespaceDelimiter = (namespace) => (_.endsWith(namespace, '=>') ? namespace : `${namespace} =>`);

/**
 * Adds a space to the end of the string
 * @param {string} namespace
 */
const _addTrailingSpace = (namespace) => `${namespace} `;
